import { makeAutoObservable } from 'mobx';

import { axiosClient } from '../config/axios-client';
import { GameResponse } from '../model/GameResponse';
import { waitUntilWithInterval } from '../utils/async.utils';
import botErrorStore from './BotErrorStore';
import chatStore from './ChatStore';
import eventLogsStore from './EventLogsStore';
import { GameState } from './state/GameState';
import { GameTable } from './state/GameTable';
import { StateStore } from './StateStore';
import tableStore from './TableStore';

const BASE_GAMES_URL = '/games';

interface GameLock {
  tableId: string;
}

class GameStore implements StateStore {
  public activeGames = new Map<string, GameState>();
  private gameLock: GameLock[] = [];

  public reloadGame = 0;

  constructor() {
    makeAutoObservable(this);
  }

  public resetState = (): void => {
    this.activeGames = new Map<string, GameState>();
    this.gameLock = [];
    this.reloadGame = 0;
  };

  public addActiveGame = (tableId: string, game: GameState): void => {
    this.activeGames.set(tableId, game);
  };

  public findActiveGame = async (tableId: string): Promise<GameState> => {
    await waitUntilWithInterval(
      () => !this.gameLock.find(lock => lock.tableId === tableId),
    );
    this.gameLock.push({ tableId });
    return new Promise(resolve => {
      tableStore.findActiveTableById(tableId).then(table => {
        const game = this.findInMemoryByTableId(tableId);
        if (game) {
          resolve(game);
        } else if (table.gameId) {
          this.loadActiveGame(table).then(response => resolve(response));
        } else {
          const game = new GameState();
          game.setTable(table);
          this.addActiveGame(tableId, game);
          resolve(game);
        }
      });
    }).finally(() => {
      const indexToRemove = this.gameLock.findIndex(
        lock => lock.tableId === tableId,
      );
      if (indexToRemove > -1) {
        this.gameLock.splice(indexToRemove, 1);
      }
    }) as Promise<GameState>;
  };

  private loadActiveGame = (table: GameTable): Promise<GameState> =>
    axiosClient
      .get<GameResponse>(`${BASE_GAMES_URL}/active?tableId=${table.id}`)
      .then(response => response.data)
      .then(response => {
        const gameState = new GameState();
        gameState.initGame(response);

        table.setPlayers(gameState.players);
        table.setDealerPid(gameState.table.dealerPid);
        gameState.setTable(table);

        this.replaceGame(table.id, gameState);
        return gameState;
      });

  public clearAllGames = (): void => {
    this.activeGames = new Map<string, GameState>();
  };

  public removeGameByTableId = (tableId: string): void => {
    this.activeGames.delete(tableId);
  };

  public clearGameData = (tableId: string): void => {
    this.removeGameByTableId(tableId);
    chatStore.removeMessages(tableId);
    botErrorStore.removeBotErrors(tableId);
    eventLogsStore.removeLogs(tableId);
  };

  public clearGameDataExcludeChatAndBotErrors = (tableId: string): void => {
    this.removeGameByTableId(tableId);
    eventLogsStore.removeLogs(tableId);
  };

  private findInMemoryByTableId = (tableId: string): GameState | undefined =>
    this.activeGames.get(tableId);

  public replaceGame = (tableId: string, newGame: GameState): void => {
    this.activeGames.set(tableId, newGame);
  };

  public setActiveGames = (activeGames: GameState[]): void => {
    this.activeGames = new Map(activeGames.map(game => [game.table.id, game]));
  };

  public forceReloadGame = (): void => {
    this.reloadGame++;
  };

  public resetReloadGame = (): void => {
    this.reloadGame = 0;
  };
}

const gameStore = new GameStore();
export default gameStore;
